import { PDFDocument, StandardFonts, rgb, PDFPage, PDFFont } from 'pdf-lib';
import {
  COORDS_ANNULLAMENTO,
  FONT_SIZE_ANNULLAMENTO,
} from './pdfCoordinatesAnnullamento.ts';
import { getPolizzaEliminata } from './getPolizzaEliminata.ts';

const COLOR_TXT = rgb(0, 0, 0);

/* Data odierna in formato gg/mm/aaaa */
function oggiFormattato(): string {
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

/* Converte ISO → gg/mm/aaaa */
function formatDateISO(d?: string | null): string {
  return d ? new Date(d).toLocaleDateString('it-IT') : '';
}

/** Utility per scrivere testo con allineamento */
function drawAlignedText(
  page: PDFPage,
  value: string,
  key: keyof typeof COORDS_ANNULLAMENTO,
  font: PDFFont,
  size: number,
  align: 'left' | 'right' = 'left',
  addDot = false
) {
  if (!value) return;

  const { x, y } = COORDS_ANNULLAMENTO[key];
  const text = addDot ? `${value}.` : value;

  let drawX = x;
  if (align === 'right') {
    const textWidth = font.widthOfTextAtSize(text, size);
    drawX = x - textWidth;
  }

  page.drawText(text, {
    x: drawX,
    y,
    size,
    font,
    color: COLOR_TXT,
  });
}

export async function generaPdfAnnullamento(idPolizza: string): Promise<Blob> {
  const p = await getPolizzaEliminata(idPolizza);

  const buffer = await fetch('/pdf/modelloann.pdf').then(r => r.arrayBuffer());
  const pdf    = await PDFDocument.load(buffer);

  const page   = pdf.getPage(0);
  const fontR  = await pdf.embedFont(StandardFonts.Helvetica);
  const fontB  = await pdf.embedFont(StandardFonts.HelveticaBold);

  /* --- Dati dinamici --- */
  const agenzia          = p.agente?.agenzia?.nome ?? '';
  const dataOggi         = oggiFormattato();
  const dataEliminazione = formatDateISO(p.deleted_at);
  const nominativo       = p.nome_cliente || p.cognome_cliente
    ? `${p.nome_cliente ?? ''} ${p.cognome_cliente ?? ''}`.trim()
    : p.ragione_sociale ?? '';
  const cfPiva = p.codice_fiscale ?? p.partita_iva ?? '';

  /* Riga agenzia + data → unica stringa allineata a destra */
  const agenziaDataText = `${agenzia}, ${dataOggi}`;
  const textWidth = fontR.widthOfTextAtSize(agenziaDataText, FONT_SIZE_ANNULLAMENTO);

  page.drawText(agenziaDataText, {
    x: COORDS_ANNULLAMENTO.agenziaData.x - textWidth,
    y: COORDS_ANNULLAMENTO.agenziaData.y,
    size: FONT_SIZE_ANNULLAMENTO,
    font: fontR,
    color: COLOR_TXT,
  });

  /* Altri campi */
  drawAlignedText(page, p.targa ?? '', 'targa', fontB, FONT_SIZE_ANNULLAMENTO);
  drawAlignedText(page, dataEliminazione, 'data_eliminazione', fontB, FONT_SIZE_ANNULLAMENTO, 'left', true);
  drawAlignedText(page, nominativo, 'nominativo', fontB, FONT_SIZE_ANNULLAMENTO);
  drawAlignedText(page, cfPiva, 'cf_piva', fontB, FONT_SIZE_ANNULLAMENTO);

  const bytes = await pdf.save();
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
}
